import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Badge, Card, CardContent, Grid, Stack, Chip, DraggableContainer, DraggableItem, ListItemIcon} from 'views/components';
import {Translate, translate} from 'views/module';
import {DragIndicator} from 'views/icons';

import CaregiverList from './caregiver_selection/CaregiverList';

import moment from 'moment-timezone';

const Review = (props) => {
  const {setIsStepValid, newJob, selectedCaregivers, setSelectedCaregivers, updateJobPriorities} = props;

  // Safe defaults for arrays to avoid undefined access
  const requirements = newJob.requirements || {};
  const certifications = requirements.certifications || [];
  const atLeastOneCertifications = requirements.atLeastOneCertifications || [];
  const niceToHaveCertifications = requirements.niceToHaveCertifications || [];
  const languages = requirements.languages || [];
  const niceToHaveLanguages = requirements.niceToHaveLanguages || [];

  const careNeeds = newJob.carePlan?.careNeeds || [];

  const ATTRIBUTE_KEYS = [
    'HOURLY_RATE',
    'START_DATE',
    'CERTIFICATIONS',
    'LANGUAGES',
    'CONSIDERATIONS',
  ];

  const attributeLabelMap = {
    HOURLY_RATE: translate('career.create.Price.HourlyRateLabel'),
    START_DATE: translate('career.create.StartDate.StartDate'),
    CERTIFICATIONS: translate('career.create.Requirements.CertificationLabel'),
    LANGUAGES: translate('career.create.Requirements.LanguagesDisplayLabel'),
    CONSIDERATIONS: translate('career.create.Considerations.Considerations'),
  };

  const [showDragHandle, setShowDragHandle] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);

  const [orderedAttributes, setOrderedAttributes] = React.useState(
    newJob.priorities?.length ? newJob.priorities : ATTRIBUTE_KEYS,
  );

  // Handler for drag-and-drop reordering — update local state and persist keys
  const handleAttributeDrop = React.useCallback(({removedIndex, addedIndex}) => {
    if (removedIndex === addedIndex) return;
    setOrderedAttributes((prev) => {
      const updated = prev.slice();
      updated.splice(addedIndex, 0, updated.splice(removedIndex, 1)[0]);
      updateJobPriorities(() => updated);
      return updated;
    });
  }, [updateJobPriorities]);

  const isOvernightShift = (startTime, endTime) => {
    if (!startTime || !endTime) return false;
    const start = moment(startTime);
    const end = moment(endTime);

    const startDay = start.clone().startOf('day');
    const endDay = end.clone().startOf('day');

    return endDay.isAfter(startDay);
  };

  React.useEffect(() => {
    setIsStepValid(true);
  }, [setIsStepValid]);

  React.useEffect(() => {
    if (!newJob.priorities?.length) {
      updateJobPriorities(() => orderedAttributes);
    }
  }, [newJob.priorities, orderedAttributes, updateJobPriorities]);

  const weeklyHours = React.useMemo(() => {
    const {schedule, type} = newJob.schedule;
    if (type === 'LIVEIN_EVENT') return 56;
    const {weeklySlots} = schedule;
    const totalHours = weeklySlots.reduce((total, slot) => {
      const start = moment(slot.startTime);
      const end = moment(slot.endTime);

      const hoursPerSlot = end.diff(start, 'hours', true);
      return total + Math.abs(hoursPerSlot);
    }, 0);
    return parseFloat(totalHours.toFixed(2));
  }, [newJob.schedule]);

  const formatSchedule = React.useMemo(() => {
    const {schedule, type} = newJob.schedule;

    if (type === 'LIVEIN_EVENT') {
      return translate('career.create.Schedule.ScheduleType.LIVEIN_EVENT');
    }

    const {weeklySlots} = schedule;
    const slotDescriptions = weeklySlots.map((slot) => {
      const start = moment(slot.startTime).format('h:mm A');
      const end = moment(slot.endTime).format('h:mm A');
      const dayName = translate(`career.create.Schedule.Day.${slot.day}`);
      const overnightIndicator = isOvernightShift(slot.startTime, slot.endTime) ? ' (overnight)' : '';
      return `${dayName}${overnightIndicator} ${start} - ${end}`;
    });

    return slotDescriptions.join('\n');
  }, [newJob.schedule]);

  const hourlyRate = parseFloat(newJob.price.hourlyRate);
  const weeklyEarnings = parseFloat((hourlyRate * weeklyHours).toFixed(2));

  return (
    <>
      <Box sx={{p: 3, maxWidth: 900, mx: 'auto'}}>
        <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 4, fontWeight: 600}}>
          <Translate expression="career.create.Steps.Review" />
        </Typography>
        <Stack spacing={3}>
          {/* About this role */}
          <Card>
            <CardContent sx={{p: 3}}>
              <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                <Translate expression="career.create.Review.AboutThisRole" />
              </Typography>

              <Box sx={{mb: 2}}>
                <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
                  <Translate expression="career.create.Review.ClientFor" />
                </Typography>
                <Typography variant="body1" sx={{mb: 2}}>
                  {newJob.client.clientName}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
                  <Translate expression="career.create.Review.Description" />
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{mb: 2}}>
                  {newJob.description?.short}
                </Typography>
                {
                  newJob.description?.long?.blob &&
                <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'pre-wrap'}}>
                  {newJob.description.long.blob}
                </Typography>
                }
              </Box>
            </CardContent>
          </Card>

          {/* Schedule and Compensation */}
          <Grid container spacing={0}>
            <Grid item xs={12} sm={6} sx={{pr: {sm: 1.5}}}>
              <Card sx={{height: '100%', minHeight: '140px'}}>
                <CardContent sx={{p: 3}}>
                  <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Steps.Schedule" />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{fontSize: '15px', whiteSpace: 'pre-line'}}>
                    {formatSchedule}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{fontSize: '14px'}}>
                    <Translate expression="career.create.Review.WeeklyHours" substitute={{hours: weeklyHours}} />
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} sx={{pl: {sm: 1.5}}}>
              <Card sx={{height: '100%', bgcolor: '#fef7f7', minHeight: '140px'}}>
                <CardContent sx={{p: 3}}>
                  <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Review.Compensation" />
                  </Typography>
                  <Typography variant="h4" sx={{fontWeight: 'bold', color: '#dc2626', mb: 1}}>
                    <Translate expression="career.create.Review.HourlyRate" substitute={{rate: hourlyRate}} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{fontSize: '16px'}}>
                    <Translate expression="career.create.Review.WeeklyEarnings" substitute={{amount: weeklyEarnings}} />
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Location and Start Date */}
          <Grid container spacing={0}>
            <Grid item xs={12} sm={6} sx={{pr: {sm: 1.5}}}>
              <Card sx={{height: '100%', minHeight: '120px'}}>
                <CardContent sx={{p: 3}}>
                  <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Steps.Location" />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{fontSize: '15px'}}>
                    <Translate
                      expression="career.create.Review.LocationDisplay"
                      substitute={{
                        neighborhood: newJob.location.neighborhood,
                        city: newJob.location.city,
                      }}
                    />
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} sx={{pl: {sm: 1.5}}}>
              <Card sx={{height: '100%', minHeight: '120px'}}>
                <CardContent sx={{p: 3}}>
                  <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Steps.StartDate" />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{fontSize: '15px'}}>
                    {translate(`career.create.StartDate.Options.${newJob.startDate.startTime}.Label`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{fontSize: '14px'}}>
                    <Translate expression="career.create.Review.FlexibleStartTiming" />
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Care needs */}
          <Card>
            <CardContent sx={{p: 3}}>
              <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                <Translate expression="career.create.Review.CareNeeds" />
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {
                  careNeeds.map((need) => (
                    <Chip
                      key={need}
                      label={translate(`career.create.CarePlan.Options.${need}.Label`)}
                      variant="filled"
                      sx={{bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 500, mb: 1}}
                    />
                  ))
                }
              </Stack>
            </CardContent>
          </Card>

          {/* Requirements */}
          <Card>
            <CardContent sx={{p: 3}}>
              <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                <Translate expression="career.create.Steps.Requirements" />
              </Typography>

              {/* Certifications */}
              <Box sx={{mb: 2}}>
                <Typography variant="subtitle1" sx={{fontWeight: 800, mb: 1}}>
                  <Translate expression="career.create.Requirements.CertificationLabel" />
                </Typography>

                {/* Mandatory Certifications */}
                {
                  certifications.length > 0 &&
                  <Box sx={{mb: 1}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                      <Translate expression='career.create.Requirements.ColorKeyMandatory'/>
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {
                        certifications.map((cert) => (
                          <Chip
                            key={cert}
                            label={translate(`career.create.Requirements.CertificationsOptions.${cert}`)}
                            variant="filled"
                            sx={{bgcolor: '#FCF2E8', color: '#FC8301', fontWeight: 500, mb: 1}}
                          />
                        ))
                      }
                    </Stack>
                  </Box>
                }

                {/* At Least One Certifications */}
                {
                  atLeastOneCertifications.length > 0 &&
                  <Box sx={{mb: 1}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                      {translate('career.create.Requirements.ColorKeyAtLeastOne')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {
                        atLeastOneCertifications.map((cert) => (
                          <Chip
                            key={cert}
                            label={translate(`career.create.Requirements.CertificationsOptions.${cert}`)}
                            variant="filled"
                            sx={{bgcolor: '#FEE5E6', color: '#F9575C', fontWeight: 500, mb: 1}}
                          />
                        ))
                      }
                    </Stack>
                  </Box>
                }

                {/* Nice to Have Certifications */}
                {
                  niceToHaveCertifications.length > 0 &&
                  <Box>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                      {translate('career.create.Requirements.ColorKeyNiceToHave')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {
                        niceToHaveCertifications.map((cert) => (
                          <Chip
                            key={cert}
                            label={translate(`career.create.Requirements.CertificationsOptions.${cert}`)}
                            variant="filled"
                            sx={{bgcolor: '#FFF8E6', color: '#FFB400', fontWeight: 500, mb: 1}}
                          />
                        ))
                      }
                    </Stack>
                  </Box>
                }
              </Box>

              {/* Languages */}
              {
                (languages.length > 0 ||
               niceToHaveLanguages.length > 0) &&
              <Box>
                <Typography variant="subtitle1" sx={{fontWeight: 800, mb: 1}}>
                  <Translate expression="career.create.Requirements.LanguagesDisplayLabel" />
                </Typography>

                {/* Mandatory Languages */}
                {
                  languages.length > 0 &&
                  <Box sx={{mb: 1}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                      {translate('career.create.Requirements.ColorKeyMandatory')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {
                        languages.map((lang) => (
                          <Chip
                            key={lang}
                            label={translate(`career.create.Requirements.LanguageOptions.${lang}`)}
                            variant="filled"
                            sx={{bgcolor: '#FCF2E8', color: '#FC8301', fontWeight: 500, mb: 1}}
                          />
                        ))
                      }
                    </Stack>
                  </Box>
                }

                {/* Nice to Have Languages */}
                {
                  niceToHaveLanguages.length > 0 &&
                  <Box>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5}}>
                      {translate('career.create.Requirements.ColorKeyNiceToHave')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {
                        niceToHaveLanguages.map((lang) => (
                          <Chip
                            key={lang}
                            label={translate(`career.create.Requirements.LanguageOptions.${lang}`)}
                            variant="filled"
                            sx={{bgcolor: '#FFF8E6', color: '#FFB400', fontWeight: 500, mb: 1}}
                          />
                        ))
                      }
                    </Stack>
                  </Box>
                }
              </Box>
              }
            </CardContent>
          </Card>

          {/* Special considerations - Only section that can be empty */}
          <Card>
            <CardContent sx={{p: 3}}>
              <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                <Translate expression="career.create.Review.SpecialConsiderations" />
              </Typography>
              <Stack spacing={2}>
                {
                  newJob.considerations?.options?.length > 0 &&
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {
                    newJob.considerations.options.map((option) => (
                      <Chip
                        key={option}
                        label={translate(`career.create.Considerations.Options.${option}`)}
                        variant="filled"
                        sx={{bgcolor: '#fff3e0', color: '#ef6c00', fontWeight: 500, mb: 1}}
                      />
                    ))
                  }
                </Stack>
                }
                {
                  newJob.considerations?.additional &&
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Review.AdditionalConsiderations" />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {newJob.considerations.additional}
                  </Typography>
                </Box>
                }
                {
                  (!newJob.considerations?.options?.length && !newJob.considerations?.additional) &&
                <Typography variant="body2" color="text.secondary">
                  <Translate expression="career.create.Review.NoSpecialConsiderations" />
                </Typography>
                }
              </Stack>
            </CardContent>
          </Card>

          <Grid container spacing={0} alignItems="flex-start">
            {/* Attributes ordering - full width row */}
            <Grid item xs={12} sm={12} sx={{display: 'flex', pr: {sm: 1.5}, mb: {xs: 2, sm: 0}}}>
              <Card sx={{mb: 0, flex: 1, width: '100%'}}>
                <CardContent sx={{p: 3}}>
                  <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
                    <Translate expression="career.create.Review.OrderAttributes" />
                  </Typography>
                  <Box>
                    <DraggableContainer
                      dragHandleSelector='.drag-handle'
                      lockAxis='y'
                      onDragStart={() => setIsDragging(true)}
                      onDragEnd={() => setIsDragging(false)}
                      onDrop={(params) => {
                        handleAttributeDrop(params); setIsDragging(false);
                      }}
                    >
                      {
                        orderedAttributes.map((key, index) => (
                          <DraggableItem key={`${key}-${index}`}>
                            <Stack sx={{p: 1.25}} >
                              <Stack direction='row' spacing={1} alignItems='center' >
                                <ListItemIcon className='drag-handle' sx={{display: 'flex', justifyContent: 'center'}}>
                                  <Badge
                                    badgeContent={
                                    (isDragging || showDragHandle) ? <DragIndicator/> : index + 1
                                    }
                                    color='primary'
                                    data-testid='taskBadge'
                                    aria-label='task-number'
                                    onMouseOver={() => setShowDragHandle(true)}
                                    onMouseLeave={() => setShowDragHandle(false)}
                                    sx={{
                                      '& .MuiBadge-badge': {
                                        'fontSize': '16px',
                                        'height': '32px',
                                        'width': '32px',
                                        'borderRadius': '20px',
                                        'color': (isDragging || showDragHandle) ? 'rgba(0, 0, 0, 0.54)' : undefined,
                                        'bgcolor': (isDragging || showDragHandle) ? 'transparent' : 'primary',
                                        'cursor': isDragging ? 'grabbing' : 'grab',
                                      },
                                    }}
                                  />
                                </ListItemIcon>
                                <Typography>
                                  {attributeLabelMap[key]}
                                </Typography>
                              </Stack>
                            </Stack>
                          </DraggableItem>
                        ))
                      }
                    </DraggableContainer>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                    <Translate expression="career.create.Review.OrderAttributesHelp" />
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        </Stack>
      </Box>

      {/* Caregiver Notification Selection - full width outside container */}
      <Box sx={{px: 3, mt: 3}}>
        <Typography variant="h6" gutterBottom sx={{fontWeight: 600}}>
          <Translate expression="career.create.Review.NotifyCaregivers" />
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          <Translate expression="career.create.Review.NotifyCaregiversHelp" />
        </Typography>
        <CaregiverList jobLocation={newJob.location} selectedCaregivers={selectedCaregivers} setSelectedCaregivers={setSelectedCaregivers}/>
      </Box>
    </>
  );
};

Review.propTypes = {
  setIsStepValid: PropTypes.func.isRequired,
  newJob: PropTypes.object.isRequired,
  selectedCaregivers: PropTypes.array,
  setSelectedCaregivers: PropTypes.func,
  updateJobPriorities: PropTypes.func,
};

export default Review;
